/**
 * Console layout of Figma CMS-02: a 216 px dark sidebar (brand, grouped nav, operator card)
 * next to the light data area. Flat colors, no shadows (docs/06 §1).
 */
import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useAuth } from '../../auth/AuthProvider';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { Button } from '../ui/button';
import { visibleNav } from './nav';
import { RESULTS_ANCHOR_ID, ResultsAnchorProvider } from './resultsAnchor';

function Dot({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn('size-1.5 shrink-0 rounded-full', active ? 'bg-pink' : 'bg-white/25')}
    />
  );
}

function initials(name: string | null, email: string | null): string {
  const source = (name ?? email ?? '?').trim();
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').toUpperCase() + (parts[1]?.[0] ?? '').toUpperCase();
}

export function Sidebar() {
  const { t } = useT();
  const { me, role, signOut } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const groups = visibleNav(role);

  return (
    <nav
      aria-label={t('cms:nav.label')}
      className="flex w-[216px] shrink-0 flex-col bg-sidebar text-white"
    >
      <div className="flex items-center gap-2.5 px-4 py-4">
        <span aria-hidden="true" className="size-4 rounded-full bg-pink" />
        <span>
          <span className="block text-[15px] leading-4 font-bold">{t('common:app.name')}</span>
          <span className="block text-[12px] tracking-wide text-white/60 uppercase">CMS</span>
        </span>
      </div>

      <div className="flex-1 overflow-y-auto pb-4">
        {groups.map((group) => (
          <div key={group.key ?? 'root'} className="mt-1">
            {group.key && (
              <p className="px-4 pt-4 pb-1 text-[12px] font-semibold tracking-wider text-white/60 uppercase">
                {t(`cms:nav.groups.${group.key}` as 'cms:nav.groups.catalog')}
              </p>
            )}
            <ul className="px-2">
              {group.items.map((item) => {
                const label = t(`cms:nav.${item.key}` as 'cms:nav.dashboard');
                if (!item.to) {
                  return (
                    <li key={item.key}>
                      <span
                        aria-disabled="true"
                        // `aria-disabled`, so the 1.4.3 exception applies: it stays dimmer than
                        // the links it sits among on purpose.
                        className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-white/35"
                      >
                        <Dot active={false} />
                        <span className="flex-1">{label}</span>
                        <span className="rounded bg-white/10 px-1.5 py-0.5 text-[12px] tracking-wide uppercase">
                          {t('cms:nav.soon')}
                        </span>
                      </span>
                    </li>
                  );
                }
                const active = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);
                return (
                  <li key={item.key}>
                    <Link
                      to={item.to}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px]',
                        active
                          ? 'bg-sidebar-2 font-semibold text-white'
                          : 'text-white/70 hover:bg-white/5 hover:text-white',
                      )}
                    >
                      <Dot active={active} />
                      {label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className="m-2 rounded-[12px] bg-sidebar-2 p-2.5">
        <div className="flex items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-full bg-pink text-[12px] font-bold text-white">
            {initials(me?.displayName ?? null, me?.email ?? null)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12px] font-semibold">
              {me?.displayName ?? me?.email ?? '—'}
            </span>
            <span className="block text-[12px] text-white/60">
              {role ? t(`cms:roles.${role}` as 'cms:roles.admin') : ''}
            </span>
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="mt-1.5 w-full justify-start text-white/60 hover:bg-white/5 hover:text-white"
          onClick={() => void signOut()}
        >
          {t('common:actions.logout')}
        </Button>
      </div>
    </nav>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4 px-6 pt-5 pb-4">
      <div>
        <h1 className="text-[22px] leading-7 font-bold text-ink">{title}</h1>
        {subtitle ? <p className="mt-1 text-[12px] text-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/** Id of the `<main>` region the skip link jumps to. */
export const MAIN_CONTENT_ID = 'main-content';

/**
 * First focusables of the document: without them the 12 sidebar links sit between every page
 * load and the table, so reaching the data costs 13+ tabs (QA 2026-09-15 UX-206) — and the
 * toolbar, the filters, the chips, «Exportar CSV» and the pager cost 24 to 62 more once
 * `<main>` has focus (UX-568). The second link is only rendered on a route that has a list.
 * Visually hidden until they take focus.
 */
function SkipLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <a
      href={`#${to}`}
      className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:border focus:border-line focus:bg-panel focus:px-3 focus:py-2 focus:text-[13px] focus:font-semibold focus:text-ink"
      onClick={(event) => {
        // Move focus explicitly: a hash jump alone leaves the caret where it was in some browsers.
        // `focus()` scrolls the region into view on its own.
        event.preventDefault();
        document.getElementById(to)?.focus();
      }}
    >
      {children}
    </a>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useT();
  return (
    <ResultsAnchorProvider>
      {(hasResults) => (
        <div className="flex h-full min-h-dvh bg-canvas">
          <SkipLink to={MAIN_CONTENT_ID}>{t('cms:nav.skipToContent')}</SkipLink>
          {hasResults && <SkipLink to={RESULTS_ANCHOR_ID}>{t('cms:nav.skipToResults')}</SkipLink>}
          <Sidebar />
          <main id={MAIN_CONTENT_ID} tabIndex={-1} className="min-w-0 flex-1 overflow-x-hidden">
            {children}
          </main>
        </div>
      )}
    </ResultsAnchorProvider>
  );
}
